import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Menu,
} from '@mui/material';
import {
  Search,
  Download,
  Add,
  MoreVert,
  Visibility,
  Edit,
  TrendingUp,
  Person,
  Schedule,
  CheckCircle,
  Email,
  Block,
  History,
} from '@mui/icons-material';

interface User {
  id: string;
  userCode: string;
  personalInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    location: string;
  };
  userType: 'Renter' | 'Host' | 'Driver' | 'Admin';
  hostType?: 'Owner' | 'Broker' | 'Fleet' | 'Liaison' | 'Manager';
  verificationStatus: 'Verified' | 'Pending' | 'Rejected';
  accountStatus: 'Active' | 'Inactive' | 'Suspended' | 'Pending';
  stats: {
    totalBookings: number;
    rating: number;
    earnings?: number;
    vehicles?: number;
    disputes: number;
  };
  joinedDate: string;
  lastActivity: string;
}

const UserManagementPageWorking: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [openProfileDialog, setOpenProfileDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [editFormData, setEditFormData] = useState<any>({});
  const [addFormData, setAddFormData] = useState<any>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    userType: '',
    hostType: '',
    accountStatus: 'Active'
  });

  // Sample users data
  const users: User[] = [
    {
      id: '1',
      userCode: 'USR001',
      personalInfo: {
        firstName: 'John',
        lastName: 'Doe',
        email: 'john.doe@email.com',
        phone: '+234 803 123 4567',
        location: 'Lagos, Nigeria',
      },
      userType: 'Host',
      hostType: 'Owner',
      verificationStatus: 'Verified',
      accountStatus: 'Active',
      stats: {
        totalBookings: 145,
        rating: 4.8,
        earnings: 125000,
        vehicles: 3,
        disputes: 2
      },
      joinedDate: '2024-01-15',
      lastActivity: '2 hours ago'
    },
    {
      id: '2',
      userCode: 'USR002',
      personalInfo: {
        firstName: 'Sarah',
        lastName: 'Johnson',
        email: 'sarah.johnson@email.com',
        phone: '+254 712 345 678',
        location: 'Nairobi, Kenya',
      },
      userType: 'Renter',
      verificationStatus: 'Verified',
      accountStatus: 'Active',
      stats: {
        totalBookings: 23,
        rating: 4.6,
        disputes: 0
      },
      joinedDate: '2024-02-28',
      lastActivity: '1 day ago'
    },
    {
      id: '3',
      userCode: 'USR003',
      personalInfo: {
        firstName: 'Michael',
        lastName: 'Chen',
        email: 'michael.chen@email.com',
        phone: '+233 244 567 890',
        location: 'Accra, Ghana',
      },
      userType: 'Host',
      hostType: 'Broker',
      verificationStatus: 'Pending',
      accountStatus: 'Pending',
      stats: {
        totalBookings: 0,
        rating: 0,
        earnings: 0,
        vehicles: 8,
        disputes: 0
      },
      joinedDate: '2024-06-20',
      lastActivity: '3 days ago'
    },
    {
      id: '4',
      userCode: 'USR004',
      personalInfo: {
        firstName: 'Fatima',
        lastName: 'Abdullahi',
        email: 'fatima.abdullahi@email.com',
        phone: '+234 813 987 6543',
        location: 'Abuja, Nigeria',
      },
      userType: 'Driver',
      verificationStatus: 'Verified',
      accountStatus: 'Active',
      stats: {
        totalBookings: 89,
        rating: 4.9,
        earnings: 78000,
        disputes: 1
      },
      joinedDate: '2024-03-10',
      lastActivity: '30 minutes ago'
    },
    {
      id: '5',
      userCode: 'ADM001',
      personalInfo: {
        firstName: 'Admin',
        lastName: 'User',
        email: 'admin@polarride.com',
        phone: '+234 800 000 0000',
        location: 'Lagos, Nigeria',
      },
      userType: 'Admin',
      verificationStatus: 'Verified',
      accountStatus: 'Active',
      stats: {
        totalBookings: 0,
        rating: 0,
        disputes: 0
      },
      joinedDate: '2023-01-01',
      lastActivity: '5 minutes ago'
    },
  ];

  const userStats = {
    totalUsers: users.length,
    activeUsers: users.filter(u => u.accountStatus === 'Active').length,
    newThisMonth: 2,
    pendingVerification: users.filter(u => u.verificationStatus === 'Pending').length
  };

  const getTabFilteredUsers = () => {
    let tabFilteredUsers = users;
    
    switch (selectedTab) {
      case 0: // All Users
        tabFilteredUsers = users;
        break;
      case 1: // Hosts
        tabFilteredUsers = users.filter(user => user.userType === 'Host');
        break;
      case 2: // Renters
        tabFilteredUsers = users.filter(user => user.userType === 'Renter');
        break;
      case 3: // Drivers
        tabFilteredUsers = users.filter(user => user.userType === 'Driver');
        break;
      default:
        tabFilteredUsers = users;
    }
    
    return tabFilteredUsers.filter(user => {
      const matchesSearch = `${user.personalInfo.firstName} ${user.personalInfo.lastName}`
        .toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.userCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.personalInfo.email.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesUserType = userTypeFilter === 'all' || user.userType === userTypeFilter;
      const matchesStatus = statusFilter === 'all' || user.accountStatus === statusFilter;
      
      return matchesSearch && matchesUserType && matchesStatus;
    });
  };

  const filteredUsers = getTabFilteredUsers();

  const getUserTypeColor = (userType: string) => {
    switch (userType) {
      case 'Host': return 'primary';
      case 'Renter': return 'secondary';
      case 'Driver': return 'success';
      case 'Admin': return 'error';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Pending': return 'warning';
      case 'Suspended': return 'error';
      case 'Inactive': return 'default';
      default: return 'default';
    }
  };

  const getVerificationColor = (status: string) => {
    switch (status) {
      case 'Verified': return 'success';
      case 'Pending': return 'warning';
      case 'Rejected': return 'error';
      default: return 'default';
    }
  };

  const handleExportUsers = () => {
    const csvContent = [
      ['User Code', 'Name', 'Email', 'User Type', 'Status', 'Bookings', 'Rating', 'Joined Date'].join(','),
      ...filteredUsers.map(user => [
        user.userCode,
        `${user.personalInfo.firstName} ${user.personalInfo.lastName}`,
        user.personalInfo.email,
        user.userType,
        user.accountStatus,
        user.stats.totalBookings,
        user.stats.rating,
        user.joinedDate
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleViewProfile = (user: User) => {
    setSelectedUser(user);
    setOpenProfileDialog(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditFormData({
      firstName: user.personalInfo.firstName,
      lastName: user.personalInfo.lastName,
      email: user.personalInfo.email,
      phone: user.personalInfo.phone,
      location: user.personalInfo.location,
      userType: user.userType,
      hostType: user.hostType || '',
      accountStatus: user.accountStatus
    });
    setOpenEditDialog(true);
  };

  const handleMoreActions = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleSaveEdit = () => {
    if (selectedUser) {
      // Security validation: Prevent external users from becoming admin
      if (editFormData.userType === 'Admin' && selectedUser.userType !== 'Admin') {
        alert('⚠️ Security Error: Cannot promote external users to Admin role. Admin privileges are reserved for PolarRide employees only.');
        return;
      }
      
      // Additional validation: Only allow admins to create other admins
      if (editFormData.userType === 'Admin') {
        alert('⚠️ Access Denied: Only existing Admin users can manage Admin roles. This action requires elevated privileges.');
        return;
      }
      
      // In a real implementation, this would make an API call
      alert(`✅ Profile updated for ${selectedUser.personalInfo.firstName} ${selectedUser.personalInfo.lastName}`);
      setOpenEditDialog(false);
      setSelectedUser(null);
    }
  };

  // Helper function to get allowed user types based on current user type
  const getAllowedUserTypes = (currentUserType: string) => {
    if (currentUserType === 'Admin') {
      // Admins can change to any type (handled by additional validation above)
      return [
        { value: 'Renter', label: 'Renter' },
        { value: 'Host', label: 'Host' },
        { value: 'Driver', label: 'Driver' },
        { value: 'Admin', label: 'Admin (PolarRide Employee)' }
      ];
    } else {
      // External users cannot become admin
      return [
        { value: 'Renter', label: 'Renter' },
        { value: 'Host', label: 'Host' },
        { value: 'Driver', label: 'Driver' }
      ];
    }
  };

  // Helper function to check if user type change is allowed
  const isUserTypeChangeAllowed = (fromType: string, toType: string) => {
    // Admin to anything: Not allowed (would need special admin confirmation)
    if (fromType === 'Admin' && toType !== 'Admin') {
      return false;
    }
    
    // External user to Admin: Not allowed
    if (fromType !== 'Admin' && toType === 'Admin') {
      return false;
    }
    
    // Other changes are allowed
    return true;
  };

  const handleSuspendUser = () => {
    if (selectedUser) {
      alert(`User ${selectedUser.personalInfo.firstName} ${selectedUser.personalInfo.lastName} has been suspended`);
      handleMenuClose();
    }
  };

  const handleSendMessage = () => {
    if (selectedUser) {
      alert(`Message sent to ${selectedUser.personalInfo.firstName} ${selectedUser.personalInfo.lastName}`);
      handleMenuClose();
    }
  };

  const handleViewBookings = () => {
    if (selectedUser) {
      alert(`Viewing bookings for ${selectedUser.personalInfo.firstName} ${selectedUser.personalInfo.lastName}`);
      handleMenuClose();
    }
  };

  const handleAddUser = () => {
    setAddFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      location: '',
      userType: '',
      hostType: '',
      accountStatus: 'Active'
    });
    setOpenAddDialog(true);
  };

  const handleSaveAdd = () => {
    // Validation
    if (!addFormData.firstName || !addFormData.lastName || !addFormData.email || !addFormData.phone || !addFormData.userType) {
      alert('Please fill in all required fields');
      return;
    }

    // Security validation: Prevent creation of admin users
    if (addFormData.userType === 'Admin') {
      alert('⚠️ Security Error: Cannot create Admin users through this interface. Admin accounts must be created through secure administrative channels.');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(addFormData.email)) {
      alert('Please enter a valid email address');
      return;
    }

    // Phone validation
    if (addFormData.phone.length < 10) {
      alert('Please enter a valid phone number');
      return;
    }

    // Generate user code
    const userCode = `USR${String(users.length + 1).padStart(3, '0')}`;

    alert(`✅ User ${addFormData.firstName} ${addFormData.lastName} created successfully!\nUser Code: ${userCode}\nVerification email sent to ${addFormData.email}`);
    setOpenAddDialog(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            User Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage users, hosts, drivers, and administrators
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExportUsers}
          >
            Export
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={handleAddUser}>
            Add User
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Users
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
                    {userStats.totalUsers}
                  </Typography>
                </Box>
                <Person sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Active Users
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: 'success.main' }}>
                    {userStats.activeUsers}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <TrendingUp sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} />
                    <Typography variant="caption" color="success.main">
                      +12%
                    </Typography>
                  </Box>
                </Box>
                <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    New This Month
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
                    {userStats.newThisMonth}
                  </Typography>
                </Box>
                <Add sx={{ fontSize: 40, color: 'info.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Pending Verification
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: 'warning.main' }}>
                    {userStats.pendingVerification}
                  </Typography>
                </Box>
                <Schedule sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {/* Tabs */}
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={`All Users (${users.length})`} />
            <Tab label={`Hosts (${users.filter(u => u.userType === 'Host').length})`} />
            <Tab label={`Renters (${users.filter(u => u.userType === 'Renter').length})`} />
            <Tab label={`Drivers (${users.filter(u => u.userType === 'Driver').length})`} />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{ minWidth: 300 }}
                InputProps={{
                  startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />
                }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>User Type</InputLabel>
                <Select
                  value={userTypeFilter}
                  onChange={(e) => setUserTypeFilter(e.target.value)}
                  label="User Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="Host">Hosts</MenuItem>
                  <MenuItem value="Renter">Renters</MenuItem>
                  <MenuItem value="Driver">Drivers</MenuItem>
                  <MenuItem value="Admin">Admins</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Suspended">Suspended</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Users Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>User Code</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Verification</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Bookings</TableCell>
                    <TableCell>Rating</TableCell>
                    <TableCell>Last Activity</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No users found matching the current filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((user) => (
                        <TableRow key={user.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar sx={{ width: 40, height: 40 }}>
                                {user.personalInfo.firstName.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {user.personalInfo.firstName} {user.personalInfo.lastName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {user.personalInfo.email}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                              {user.userCode}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.userType}
                              size="small"
                              color={getUserTypeColor(user.userType) as any}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.verificationStatus}
                              size="small"
                              color={getVerificationColor(user.verificationStatus) as any}
                              variant={user.verificationStatus === 'Verified' ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.accountStatus}
                              size="small"
                              color={getStatusColor(user.accountStatus) as any}
                              variant={user.accountStatus === 'Active' ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {user.stats.totalBookings}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {user.stats.rating > 0 ? `${user.stats.rating}/5` : 'No rating'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {user.lastActivity}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                              <IconButton 
                                size="small" 
                                onClick={() => handleViewProfile(user)}
                                sx={{ color: 'primary.main' }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                onClick={() => handleEditUser(user)}
                                sx={{ color: 'info.main' }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                onClick={(e) => handleMoreActions(e, user)}
                                sx={{ color: 'text.secondary' }}
                              >
                                <MoreVert fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* More Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleViewBookings}>
          <History sx={{ mr: 1 }} />
          View Bookings
        </MenuItem>
        <MenuItem onClick={handleSendMessage}>
          <Email sx={{ mr: 1 }} />
          Send Message
        </MenuItem>
        <MenuItem onClick={handleSuspendUser}>
          <Block sx={{ mr: 1 }} />
          Suspend User
        </MenuItem>
      </Menu>

      {/* Profile View Dialog */}
      <Dialog open={openProfileDialog} onClose={() => setOpenProfileDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 50, height: 50 }}>
              {selectedUser?.personalInfo.firstName.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h6">
                {selectedUser ? `${selectedUser.personalInfo.firstName} ${selectedUser.personalInfo.lastName}` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedUser?.userCode} • {selectedUser?.userType}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Typography variant="h6" gutterBottom>Personal Information</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}><strong>Email:</strong> {selectedUser.personalInfo.email}</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}><strong>Phone:</strong> {selectedUser.personalInfo.phone}</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}><strong>Location:</strong> {selectedUser.personalInfo.location}</Typography>
                  <Typography variant="body2"><strong>Joined:</strong> {new Date(selectedUser.joinedDate).toLocaleDateString()}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Typography variant="h6" gutterBottom>Account Status</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}><strong>Verification:</strong> {selectedUser.verificationStatus}</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}><strong>Account Status:</strong> {selectedUser.accountStatus}</Typography>
                  {selectedUser.hostType && (
                    <Typography variant="body2" sx={{ mb: 1 }}><strong>Host Type:</strong> {selectedUser.hostType}</Typography>
                  )}
                  <Typography variant="body2"><strong>Last Activity:</strong> {selectedUser.lastActivity}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Typography variant="h6" gutterBottom>Statistics</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Total Bookings</Typography>
                      <Typography variant="h6">{selectedUser.stats.totalBookings}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Rating</Typography>
                      <Typography variant="h6">{selectedUser.stats.rating > 0 ? `${selectedUser.stats.rating}/5` : 'No rating'}</Typography>
                    </Grid>
                    {selectedUser.stats.earnings && (
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" color="text.secondary">Earnings</Typography>
                        <Typography variant="h6">₦{selectedUser.stats.earnings.toLocaleString()}</Typography>
                      </Grid>
                    )}
                    {selectedUser.stats.vehicles && (
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" color="text.secondary">Vehicles</Typography>
                        <Typography variant="h6">{selectedUser.stats.vehicles}</Typography>
                      </Grid>
                    )}
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Disputes</Typography>
                      <Typography variant="h6" color={selectedUser.stats.disputes > 0 ? 'error.main' : 'success.main'}>
                        {selectedUser.stats.disputes}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenProfileDialog(false)}>Close</Button>
          <Button variant="outlined" onClick={() => {
            setOpenProfileDialog(false);
            selectedUser && handleEditUser(selectedUser);
          }}>Edit Profile</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User Profile</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                value={editFormData.firstName || ''}
                onChange={(e) => setEditFormData({...editFormData, firstName: e.target.value})}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={editFormData.lastName || ''}
                onChange={(e) => setEditFormData({...editFormData, lastName: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editFormData.email || ''}
                onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={editFormData.phone || ''}
                onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                value={editFormData.location || ''}
                onChange={(e) => setEditFormData({...editFormData, location: e.target.value})}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>User Type</InputLabel>
                <Select
                  value={editFormData.userType || ''}
                  onChange={(e) => {
                    const newType = e.target.value;
                    if (selectedUser && !isUserTypeChangeAllowed(selectedUser.userType, newType)) {
                      if (selectedUser.userType === 'Admin' && newType !== 'Admin') {
                        alert('⚠️ Cannot demote Admin users. This action requires special administrative approval.');
                        return;
                      }
                      if (selectedUser.userType !== 'Admin' && newType === 'Admin') {
                        alert('⚠️ Cannot promote external users to Admin. Admin roles are reserved for PolarRide employees.');
                        return;
                      }
                    }
                    setEditFormData({...editFormData, userType: newType});
                  }}
                  label="User Type"
                >
                  {getAllowedUserTypes(selectedUser?.userType || '').map((type) => (
                    <MenuItem 
                      key={type.value} 
                      value={type.value}
                      disabled={selectedUser && !isUserTypeChangeAllowed(selectedUser.userType, type.value)}
                    >
                      {type.label}
                      {type.value === 'Admin' && selectedUser?.userType !== 'Admin' && (
                        <Typography component="span" sx={{ ml: 1, color: 'error.main', fontSize: '0.8em' }}>
                          (Restricted)
                        </Typography>
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedUser?.userType !== 'Admin' && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  ℹ️ Admin role is restricted to PolarRide employees only
                </Typography>
              )}
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Account Status</InputLabel>
                <Select
                  value={editFormData.accountStatus || ''}
                  onChange={(e) => setEditFormData({...editFormData, accountStatus: e.target.value})}
                  label="Account Status"
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Suspended">Suspended</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {editFormData.userType === 'Host' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Host Type</InputLabel>
                  <Select
                    value={editFormData.hostType || ''}
                    onChange={(e) => setEditFormData({...editFormData, hostType: e.target.value})}
                    label="Host Type"
                  >
                    <MenuItem value="Owner">Owner</MenuItem>
                    <MenuItem value="Broker">Broker</MenuItem>
                    <MenuItem value="Fleet">Fleet</MenuItem>
                    <MenuItem value="Liaison">Liaison</MenuItem>
                    <MenuItem value="Manager">Manager</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveEdit}
            disabled={selectedUser && editFormData.userType === 'Admin' && selectedUser.userType !== 'Admin'}
          >
            Save Changes
          </Button>
          {selectedUser && editFormData.userType === 'Admin' && selectedUser.userType !== 'Admin' && (
            <Typography variant="caption" color="error" sx={{ ml: 2 }}>
              Admin promotion blocked
            </Typography>
          )}
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                value={addFormData.firstName}
                onChange={(e) => setAddFormData({...addFormData, firstName: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={addFormData.lastName}
                onChange={(e) => setAddFormData({...addFormData, lastName: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={addFormData.email}
                onChange={(e) => setAddFormData({...addFormData, email: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={addFormData.phone}
                onChange={(e) => setAddFormData({...addFormData, phone: e.target.value})}
                placeholder="+234 803 123 4567"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                value={addFormData.location}
                onChange={(e) => setAddFormData({...addFormData, location: e.target.value})}
                placeholder="e.g., Lagos, Nigeria"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel>User Type</InputLabel>
                <Select
                  value={addFormData.userType}
                  onChange={(e) => setAddFormData({...addFormData, userType: e.target.value})}
                  label="User Type"
                >
                  <MenuItem value="Renter">Renter</MenuItem>
                  <MenuItem value="Host">Host</MenuItem>
                  <MenuItem value="Driver">Driver</MenuItem>
                  <MenuItem value="Admin" disabled>
                    Admin (Restricted)
                  </MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                ℹ️ Admin accounts cannot be created through this interface
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Account Status</InputLabel>
                <Select
                  value={addFormData.accountStatus}
                  onChange={(e) => setAddFormData({...addFormData, accountStatus: e.target.value})}
                  label="Account Status"
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Suspended">Suspended</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {addFormData.userType === 'Host' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Host Type</InputLabel>
                  <Select
                    value={addFormData.hostType}
                    onChange={(e) => setAddFormData({...addFormData, hostType: e.target.value})}
                    label="Host Type"
                  >
                    <MenuItem value="Owner">Owner</MenuItem>
                    <MenuItem value="Broker">Broker</MenuItem>
                    <MenuItem value="Fleet">Fleet</MenuItem>
                    <MenuItem value="Liaison">Liaison</MenuItem>
                    <MenuItem value="Manager">Manager</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveAdd}
            disabled={!addFormData.firstName || !addFormData.lastName || !addFormData.email || !addFormData.phone || !addFormData.userType}
          >
            Add User
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagementPageWorking;